import { appError, getDeviceLocale, err, ok, PLAN_TIERS, } from '@orbit-mind/shared-types';
import { outlineSchema, parseAt, parseDocument } from '@orbit-mind/validators';
import * as z from 'zod';
export function pdfMaterialText(material) {
    return material.pages
        .map((page) => `# Page ${String(page.page)}\n${page.text.replace(/\s+/g, ' ').trim()}`)
        .join('\n');
}
const PROVIDERS = z.object({
    providers: z.array(z.object({ id: z.string().min(1), label: z.string().min(1) })),
    /*
     * The closed beta, as two booleans. Optional because a deployment running an older sign-in
     * Worker does not send them, and the honest reading of their absence is "no gate".
     */
    inviteRequired: z.boolean().optional(),
    inviteHeld: z.boolean().optional(),
    passwordEnabled: z.boolean().optional(),
});
const USER = z.object({
    id: z.string().min(1),
    email: z.string().min(1),
    displayName: z.string(),
    firebasePassword: z.boolean().optional(),
});
const SESSION = z.object({ user: USER, session: z.object({ expiresAt: z.number() }) });
const APP_SESSION = z.object({
    token: z.string().min(16),
    user: USER,
    session: z.object({ expiresAt: z.number() }),
});
const GENERATED = z
    .object({
    prompt: z.string(),
    outline: outlineSchema,
    document: z.unknown(),
    summary: z
        .object({
        nodeCount: z.number().int().nonnegative(),
        depth: z.number().int().nonnegative(),
        trimmed: z.boolean(),
    })
        .strict(),
    model: z.string(),
    remaining: z.number().int().nonnegative(),
    // Only when asked for (`withSources`), and only from a server that knows how to look up.
    sources: z
        .array(z
        .object({ title: z.string().max(200), url: z.url({ protocol: /^https?$/ }).max(2048) })
        .strict())
        .max(10)
        .optional(),
})
    .strict();
const PLAN = z.object({
    tier: z.enum(PLAN_TIERS),
    source: z.enum(['free', 'store', 'beta']),
    credits: z.object({
        monthly: z.number().int().nonnegative(),
        used: z.number().int().nonnegative(),
        remaining: z.number().int().nonnegative(),
        periodStart: z.number(),
        renewsAt: z.number(),
    }),
    subscription: z
        .object({
        productId: z.string(),
        expiresAt: z.number().nullable(),
        willRenew: z.boolean(),
        billingIssue: z.boolean(),
    })
        .nullable(),
});
function unreachable(what, cause) {
    return appError('NETWORK_UNAVAILABLE', `${what} could not be reached`, {
        retryable: true,
        cause,
    });
}
/**
 * The server's own code and message when it sent one.
 *
 * "You have generated your limit for today" is a sentence a person can act on, and a generic
 * failure is not. Anything else falls back to a refusal whose retryability follows the status.
 */
function refusalFrom(body, status, fallback) {
    if (typeof body === 'object' && body !== null && 'code' in body && 'message' in body) {
        const { code, message, details } = body;
        const refusal = { code: code, message, retryable: status >= 500 };
        // What the server says about the refusal — which limit, and when it renews — so the
        // interface can say it in the person's language. Only plain values; anything else is dropped.
        const kept = plainDetails(details);
        return kept === undefined ? refusal : { ...refusal, details: kept };
    }
    return appError('INTERNAL', fallback, { retryable: status >= 500 });
}
function plainDetails(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return undefined;
    const kept = {};
    for (const [key, entry] of Object.entries(value)) {
        if (typeof entry === 'string' || typeof entry === 'number' || typeof entry === 'boolean') {
            kept[key] = entry;
        }
    }
    return Object.keys(kept).length === 0 ? undefined : kept;
}
/** The path that starts a web sign-in. A link, because the handshake cookie needs a navigation. */
export function signInPath(providerId, returnTo, consent) {
    return `/auth/${encodeURIComponent(providerId)}/start?returnTo=${encodeURIComponent(returnTo)}${consentQuery(consent)}`;
}
/** Consent, as the start of a sign-in carries it: `&terms=<version>&news=0|1`, or nothing. */
function consentQuery(consent) {
    if (consent === undefined)
        return '';
    return `&terms=${encodeURIComponent(consent.terms)}&news=${consent.news ? '1' : '0'}`;
}
/**
 * The path that starts a sign-in from the phone app.
 *
 * `challenge` is the SHA-256 of a verifier the app keeps; `invite` is the closed beta's code,
 * which on a phone has to ride on this URL because the app's `fetch` and its sign-in browser do
 * not share cookies.
 */
export function appSignInPath(providerId, challenge, invite, consent) {
    // Built by hand rather than with `URLSearchParams`: React Native's implements `append` and
    // `toString` and throws on `set` and `get`, and this function runs on the phone.
    const parts = ['client=app', `challenge=${encodeURIComponent(challenge)}`];
    const code = invite?.trim() ?? '';
    if (code !== '')
        parts.push(`invite=${encodeURIComponent(code)}`);
    return `/auth/${encodeURIComponent(providerId)}/start?${parts.join('&')}${consentQuery(consent)}`;
}
/** Where a link takes a browser: the page, not the API it reads from. */
export function sharedMapPath(token) {
    return `/s/${encodeURIComponent(token)}`;
}
const SHARE = z.object({ token: z.string().min(1), createdAt: z.number() }).strict();
const SHARE_ANSWER = z.object({ share: SHARE.nullable() }).strict();
const SHARED_MAP = z
    .object({
    title: z.string(),
    revision: z.number(),
    updatedAt: z.number(),
    document: z.unknown(),
    authorKey: z.string().optional(),
})
    .strict();
export function createApiClient(options) {
    const readOwnAi = options.ownAi;
    const base = options.origin ?? '';
    const deviceLocale = options.deviceLocale ?? getDeviceLocale;
    const send = (path, init = {}) => options.fetch(`${base}${path}`, {
        ...init,
        headers: { accept: 'application/json', ...init.headers },
        // Explicit, though it is the default: in a browser these requests are only useful if they
        // carry the cookie. React Native ignores it, and carries a bearer header instead.
        credentials: 'same-origin',
    });
    const postJson = (path, body) => send(path, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(body),
    });
    async function getJson(path, schema, init = {}) {
        try {
            const response = await send(path, init);
            if (!response.ok)
                return null;
            const parsed = parseAt(schema, await response.json(), 'network', path);
            return parsed.ok ? parsed.value : null;
        }
        catch {
            return null;
        }
    }
    /** One call about a map's link, answered with the link or with nothing. */
    async function shareCall(request) {
        let response;
        try {
            response = await request();
        }
        catch (cause) {
            return err(unreachable('sharing', cause));
        }
        const body = await response.json().catch(() => null);
        if (!response.ok)
            return err(refusalFrom(body, response.status, 'sharing is not available'));
        const parsed = parseAt(SHARE_ANSWER, body, 'network', 'share');
        if (!parsed.ok)
            return err(parsed.error);
        return ok(parsed.value.share);
    }
    return {
        async importPdf(bytes) {
            try {
                const response = await send('/ai/import-pdf', {
                    method: 'POST',
                    headers: { 'content-type': 'application/pdf' },
                    body: bytes instanceof Uint8Array ? new Uint8Array(bytes) : bytes,
                    signal: AbortSignal.timeout(60000),
                });
                const body = await response.json();
                if (!response.ok)
                    return err(refusalFrom(body, response.status, 'PDF import unavailable'));
                const parsed = z
                    .object({
                    pages: z
                        .array(z.object({ page: z.number().int().positive(), text: z.string().max(45000) }))
                        .max(100),
                    totalPages: z.number().int().min(1).max(100),
                })
                    .safeParse(body);
                return parsed.success
                    ? ok(parsed.data)
                    : err(appError('VALIDATION_FAILED', 'Invalid PDF extraction'));
            }
            catch (cause) {
                return err(unreachable('PDF import', cause));
            }
        },
        async deleteAccount(userId, confirmation, password) {
            try {
                const response = await send('/auth/delete-account', {
                    method: 'POST',
                    headers: { 'content-type': 'application/json', 'x-orbit-account': userId },
                    body: JSON.stringify({ confirmation, ...(password === undefined ? {} : { password }) }),
                });
                if (response.status !== 202)
                    return err(refusalFrom(await response.json().catch(() => null), response.status, 'Account deletion could not be confirmed.'));
                return ok(undefined);
            }
            catch (cause) {
                return err(unreachable('account deletion', cause));
            }
        },
        async readAccount() {
            const controller = new AbortController();
            const timeout = setTimeout(() => {
                controller.abort();
            }, 8000);
            try {
                const [response, listing] = await Promise.all([
                    send('/auth/session', { signal: controller.signal }).catch(() => null),
                    getJson('/auth/providers', PROVIDERS, { signal: controller.signal }),
                ]);
                if (response?.ok) {
                    const parsed = parseAt(SESSION, await response.json().catch(() => null), 'network', 'session');
                    if (parsed.ok)
                        return {
                            status: 'signed-in',
                            user: parsed.value.user,
                            expiresAt: parsed.value.session.expiresAt,
                        };
                }
                // Only an explicit 401 invalidates a credential. Offline, 5xx and malformed answers
                // must retain the current local library and must never erase a phone's session token.
                if (response?.status !== 401 ||
                    listing === null ||
                    (listing.providers.length === 0 && listing.passwordEnabled !== true))
                    return { status: 'unavailable' };
                return {
                    status: 'signed-out',
                    providers: listing.providers,
                    inviteNeeded: listing.inviteRequired === true && listing.inviteHeld !== true,
                    inviteRequired: listing.inviteRequired === true,
                    ...(listing.passwordEnabled === true ? { passwordEnabled: true } : {}),
                };
            }
            finally {
                clearTimeout(timeout);
            }
        },
        async offerInviteCode(code) {
            try {
                const response = await postJson('/auth/invite', { code });
                if (response.ok)
                    return ok(undefined);
                // One answer for "never existed" and "already used". The server gives one on purpose,
                // and repeating it is the honest thing to show.
                return err(appError('FORBIDDEN', 'That code cannot be used. Check it, or ask for another.', {
                    retryable: false,
                }));
            }
            catch (cause) {
                return err(unreachable('the sign-in', cause));
            }
        },
        async exchangeAppCode(code, verifier) {
            let response;
            try {
                response = await postJson('/auth/app/exchange', { code, verifier });
            }
            catch (cause) {
                return err(unreachable('the sign-in', cause));
            }
            const body = await response.json().catch(() => null);
            if (!response.ok) {
                return err(refusalFrom(body, response.status, 'this sign-in could not be completed'));
            }
            const parsed = parseAt(APP_SESSION, body, 'network', 'app session');
            if (!parsed.ok)
                return err(parsed.error);
            return ok({
                token: parsed.value.token,
                user: parsed.value.user,
                expiresAt: parsed.value.session.expiresAt,
            });
        },
        async signOut(scope) {
            const path = scope === 'everywhere' ? '/auth/signout-everywhere' : '/auth/signout';
            try {
                await send(path, { method: 'POST' });
            }
            catch {
                // A sign-out that never reached the Worker did not happen. Nothing is reported here
                // because nothing here can tell: the caller re-reads the account afterwards, and a
                // session that survived shows up as still signed in, which is the truth.
            }
        },
        async sendFeedback(message, context) {
            try {
                const response = await postJson('/beta/feedback', { message, context });
                if (response.ok)
                    return ok(undefined);
                return err(appError('INTERNAL', 'that could not be sent', { retryable: response.status >= 500 }));
            }
            catch (cause) {
                return err(appError('NETWORK_UNAVAILABLE', 'that could not be sent', { retryable: true, cause }));
            }
        },
        async readShare(mapId) {
            return shareCall(() => send(`/sync/maps/${encodeURIComponent(mapId)}/share`));
        },
        async share(mapId, policyVersion) {
            const answer = await shareCall(() => postJson(`/sync/maps/${encodeURIComponent(mapId)}/share`, policyVersion === undefined ? {} : { policyVersion }));
            if (!answer.ok)
                return answer;
            if (answer.value === null) {
                return err(appError('INTERNAL', 'the link could not be made', { retryable: true }));
            }
            return ok(answer.value);
        },
        async unshare(mapId) {
            const answer = await shareCall(() => postJson(`/sync/maps/${encodeURIComponent(mapId)}/unshare`, {}));
            return answer.ok ? ok(undefined) : answer;
        },
        async openShared(token) {
            let response;
            try {
                response = await send(`/share/${encodeURIComponent(token)}`);
            }
            catch (cause) {
                return err(unreachable('the shared map', cause));
            }
            const body = await response.json().catch(() => null);
            if (!response.ok) {
                return err(refusalFrom(body, response.status, 'this link does not open anything'));
            }
            const parsed = parseAt(SHARED_MAP, body, 'network', 'shared map');
            if (!parsed.ok)
                return err(parsed.error);
            const document = parseDocument(parsed.value.document, 'network', 'shared map document');
            if (!document.ok)
                return err(document.error);
            return ok({ ...parsed.value, document: document.value });
        },
        async recoverGeneration() {
            try {
                const response = await send('/ai/result');
                const body = await response.json();
                if (!response.ok)
                    return err(refusalFrom(body, response.status, 'Não foi possível recuperar o rascunho.'));
                if (body === null)
                    return ok(null);
                const parsed = parseAt(GENERATED, body, 'ai', 'recovered map');
                if (!parsed.ok)
                    return err(parsed.error);
                const document = parseDocument(parsed.value.document, 'ai', 'recovered map document');
                return document.ok
                    ? ok({ ...parsed.value, document: document.value, sources: parsed.value.sources ?? [] })
                    : err(document.error);
            }
            catch (cause) {
                return err(unreachable('the drafting service', cause));
            }
        },
        async generate(subject, options) {
            let response;
            try {
                const ownAi = await readOwnAi?.();
                response = await postJson('/ai/maps', {
                    subject,
                    ...(ownAi == null ? {} : { ownAi }),
                    locale: getDeviceLocale([deviceLocale()]),
                    ...(options === undefined ? {} : { options }),
                    withSources: true,
                });
            }
            catch (cause) {
                return err(unreachable('the drafting service', cause));
            }
            const body = await response.json().catch(() => null);
            if (!response.ok)
                return err(refusalFrom(body, response.status, 'the map could not be drafted'));
            const parsed = parseAt(GENERATED, body, 'ai', 'generated map');
            if (!parsed.ok)
                return err(parsed.error);
            // Parsed a second time, as a document: what is previewed is what accepting creates, and
            // a document that does not satisfy the domain's invariants must not reach the library.
            const document = parseDocument(parsed.value.document, 'ai', 'generated map document');
            if (!document.ok)
                return err(document.error);
            return ok({
                prompt: parsed.value.prompt,
                outline: parsed.value.outline,
                document: document.value,
                summary: parsed.value.summary,
                model: parsed.value.model,
                remaining: parsed.value.remaining,
                sources: parsed.value.sources ?? [],
            });
        },
        async readPlan() {
            let response;
            try {
                response = await send('/ai/plan');
            }
            catch (cause) {
                return err(unreachable('the plan', cause));
            }
            const body = await response.json().catch(() => null);
            if (!response.ok)
                return err(refusalFrom(body, response.status, 'the plan could not be read'));
            return parseAt(PLAN, body, 'network', 'plan');
        },
    };
}
//# sourceMappingURL=client.js.map